import { useCallback, useSyncExternalStore } from 'react';

/**
 * Active-repo *filter* state, the repo-level companion to
 * `active-org.ts`. Selecting a repo in the nav org switcher stashes its
 * id here (localStorage, reactive via `useSyncExternalStore`) so the
 * choice survives reloads and stays in sync across tabs without a
 * context provider.
 *
 * This is intentionally just storage + a reactive read for now — views
 * don't consume it yet. It's the seam the data views will read from
 * when per-repo filtering is wired through (a deliberate follow-up).
 * The filter is scoped to the active org: switching orgs clears it
 * (see `clearStoredActiveRepo`, called from the org switcher).
 */

const KEY = 'ta_active_repo';
const EVENT = 'ta:active-repo';

export function getStoredActiveRepoId(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setStoredActiveRepoId(repoId: string): void {
  try {
    localStorage.setItem(KEY, repoId);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // ignore (private mode, etc.)
  }
}

export function clearStoredActiveRepo(): void {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // ignore
  }
}

function subscribe(listener: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(EVENT, listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(EVENT, listener);
  };
}

function getSnapshot(): string | null {
  return getStoredActiveRepoId();
}

function getServerSnapshot(): string | null {
  return null;
}

/**
 * Reactive active-repo-filter id + setters. `activeRepoId` is null when
 * the filter is cleared ("All repos").
 */
export function useActiveRepo() {
  const activeRepoId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const selectRepo = useCallback((repoId: string) => setStoredActiveRepoId(repoId), []);
  const clearRepo = useCallback(() => clearStoredActiveRepo(), []);
  return { activeRepoId, selectRepo, clearRepo };
}
